import { useEffect, useRef, useState } from "react";
import { animate, useInView } from "framer-motion";
import { Quote } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { useLang } from "@/i18n";

const Counter = ({ to, format }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [val, setVal] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, to, {
      duration: 2,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (v) => setVal(v),
    });
    return () => controls.stop();
  }, [inView, to]);

  return <span ref={ref}>{format(val)}</span>;
};

const VALUES = [
  { to: 22, format: (v) => `${Math.round(v)}+` },
  { to: 6790, format: (v) => Math.round(v).toLocaleString("it-CH").replace(/,/g, "'") },
  { to: 24, format: (v) => `${Math.round(v)}/7` },
  { to: 100, format: (v) => `${Math.round(v)}%` },
];

export const Stats = () => {
  const { t } = useLang();
  const s = t.stats;

  return (
    <section className="relative overflow-hidden py-28">
      <span
        aria-hidden="true"
        className="text-outline-faint pointer-events-none absolute -top-6 left-0 select-none whitespace-nowrap font-display text-[22vw] font-black uppercase leading-none opacity-60"
      >
        {s.watermark}
      </span>
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <div
          data-testid="stats-grid"
          className="grid grid-cols-2 gap-px overflow-hidden rounded-3xl border border-white/10 bg-white/10 lg:grid-cols-4"
        >
          {VALUES.map((v, i) => (
            <div key={i} className="bg-[#0C0C11] p-8 sm:p-10">
              <div className="font-display text-4xl font-black text-amber-400 sm:text-5xl">
                <Counter to={v.to} format={v.format} />
              </div>
              <p className="mt-3 text-sm leading-snug text-slate-400">{s.items[i].label}</p>
            </div>
          ))}
        </div>
        <Reveal delay={0.15}>
          <figure className="mx-auto mt-20 max-w-3xl text-center">
            <Quote className="mx-auto h-8 w-8 text-amber-400/60" />
            <blockquote className="mt-6 font-display text-xl font-semibold leading-relaxed text-white sm:text-2xl">
              {s.quote}
            </blockquote>
            <figcaption className="mt-6 font-mono-alt text-[11px] uppercase tracking-[0.3em] text-amber-300">
              {s.caption}
            </figcaption>
          </figure>
        </Reveal>
      </div>
    </section>
  );
};
